import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Shield, Globe, Lock, Zap, AlertTriangle, CheckCircle, XCircle, Loader2 } from "lucide-react";

const GRADE_COLORS = { A: "text-[#00FF41]", B: "text-green-400", C: "text-yellow-400", D: "text-orange-400", F: "text-red-400" };
const STATUS_ICONS = { GOOD: CheckCircle, OK: CheckCircle, PASS: CheckCircle, WARN: AlertTriangle, SLOW: AlertTriangle, FAIL: XCircle, MISSING: XCircle, EXPOSED: AlertTriangle, BAD: XCircle, HEAVY: AlertTriangle };

export default function EdgeGuardian() {
  const [scanUrl, setScanUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runScan = async () => {
    if (!scanUrl.trim()) { toast.error("Enter a URL to scan"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await API.post("/tools/edge-guardian/scan", { url: scanUrl });
      setResult(data);
    } catch (e) { setError(e.response?.data?.detail || "Scan failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="edge-guardian-page">
      <nav className="border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">NRD<span className="text-[#00FF41]">.</span></Link>
            <span className="text-xs font-mono text-[#00FF41]">/ EDGE-GUARDIAN</span>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#00FF41] flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-1">Edge Guardian</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Security & Performance Scanner</h1>
        </div>
        <div className="flex gap-3 mb-8">
          <input type="url" value={scanUrl} onChange={e => setScanUrl(e.target.value)} placeholder="https://yoursite.com"
            className="flex-1 bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41]"
            onKeyDown={e => e.key === 'Enter' && runScan()} data-testid="scan-url-input" />
          <button onClick={runScan} disabled={loading} className="flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-6 py-3 text-sm font-bold hover:bg-[#00CC33] disabled:opacity-50" data-testid="run-scan-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />} {loading ? "Scanning..." : "Scan"}
          </button>
        </div>
        {error && <div className="p-4 bg-red-900/30 border border-red-800 text-sm text-red-400 mb-6">{error}</div>}
        {result && (
          <div className="space-y-6">
            {/* Grade */}
            <div className="flex items-center gap-6 border border-[#1E1E1E] bg-[#111111] p-6">
              <div className="text-center">
                <p className={`font-heading text-7xl font-black ${GRADE_COLORS[result.grade]}`}>{result.grade}</p>
                <p className="text-xs font-mono text-slate-500 mt-1">GRADE</p>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-400 mb-2">{result.url}</p>
                <div className="flex gap-6">
                  <div><p className="text-xs text-slate-500">Overall</p><p className="text-lg font-bold">{result.overall_score}/100</p></div>
                  <div><p className="text-xs text-slate-500">Security</p><p className="text-lg font-bold">{result.security?.score || 0}/100</p></div>
                  <div><p className="text-xs text-slate-500">Performance</p><p className="text-lg font-bold">{result.performance?.performance_score || 0}/100</p></div>
                </div>
              </div>
            </div>
            {/* SSL */}
            <div className="border border-[#1E1E1E] bg-[#111111] p-6">
              <div className="flex items-center gap-2 mb-4"><Lock size={16} className="text-[#00FF41]" /><h3 className="font-heading text-lg font-medium">SSL Certificate</h3></div>
              {result.ssl?.valid ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div><p className="text-xs text-slate-500">Status</p><p className="text-sm text-[#00FF41] font-mono">VALID</p></div>
                  <div><p className="text-xs text-slate-500">Issuer</p><p className="text-sm">{result.ssl.issuer}</p></div>
                  <div><p className="text-xs text-slate-500">Expires</p><p className="text-sm">{result.ssl.expires}</p></div>
                  <div><p className="text-xs text-slate-500">Days Left</p><p className={`text-sm font-mono ${result.ssl.days_until_expiry > 30 ? 'text-[#00FF41]' : 'text-red-400'}`}>{result.ssl.days_until_expiry}</p></div>
                </div>
              ) : <p className="text-red-400 text-sm">SSL check failed: {result.ssl?.error}</p>}
            </div>
            {/* Security Headers */}
            <div className="border border-[#1E1E1E] bg-[#111111] p-6">
              <div className="flex items-center gap-2 mb-4"><Shield size={16} className="text-[#00FF41]" /><h3 className="font-heading text-lg font-medium">Security Headers ({result.security?.passed}/{result.security?.total_checks})</h3></div>
              <div className="space-y-2">
                {result.security?.headers?.map((h, i) => {
                  const Icon = STATUS_ICONS[h.status] || AlertTriangle;
                  return (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#1E1E1E] last:border-0" data-testid={`header-${h.short_name}`}>
                      <Icon size={14} className={h.present ? 'text-[#00FF41]' : 'text-red-400'} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{h.header}</p>
                        <p className="text-xs text-slate-500">{h.description}</p>
                      </div>
                      <span className={`text-xs font-mono px-2 py-0.5 ${h.present ? 'text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30' : 'text-red-400 bg-red-900/30 border border-red-800'}`}>{h.status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Performance */}
            <div className="border border-[#1E1E1E] bg-[#111111] p-6">
              <div className="flex items-center gap-2 mb-4"><Zap size={16} className="text-[#00FF41]" /><h3 className="font-heading text-lg font-medium">Performance Tests</h3></div>
              <div className="space-y-2">
                {result.performance?.tests?.map((t, i) => {
                  const Icon = STATUS_ICONS[t.status] || CheckCircle;
                  return (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#1E1E1E] last:border-0">
                      <Icon size={14} className={t.status === 'GOOD' || t.status === 'OK' ? 'text-[#00FF41]' : t.status === 'WARN' || t.status === 'SLOW' ? 'text-yellow-400' : 'text-red-400'} />
                      <p className="text-sm flex-1">{t.name}</p>
                      <span className="text-sm font-mono text-slate-300">{t.value}</span>
                      <span className={`text-xs font-mono px-2 py-0.5 ${t.status === 'GOOD' || t.status === 'OK' ? 'text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30' : t.status === 'WARN' || t.status === 'SLOW' ? 'text-yellow-400 bg-yellow-900/30 border border-yellow-800' : 'text-red-400 bg-red-900/30 border border-red-800'}`}>{t.status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {!result && !loading && <div className="text-center py-20 text-slate-500"><Shield size={48} className="mx-auto mb-4 opacity-30" /><p>Enter a URL and click "Scan" to analyze security & performance</p></div>}
      </div>
    </div>
  );
}
